import React, { Component } from 'react';
import { Container, Row, Col, Card, Badge, Alert, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { GoRepo, GoHistory, GoGear, GoChevronRight } from 'react-icons/go';
import BlobHeader from "./header";
import BlobRevisions from "./revisions";
import BlobMarkdown from "./md";
import BlobSettings from "./settings/settings";
import BlobTabs from "components/blob/BlobTabs/index";
import BlobHeaderToggle from "components/blob/BlobHeaderToggle/index";
import ShortcutsModal from "components/blob/shortcuts/index";
import { HeaderSlotPortal } from "components/controls/HeaderSlot/index";
import Navigation from "context/nav";
import Preferences from "context/preferences";
import Title from "context/title";
import Utils from "context/utils";
import { isTypingTarget } from "context/keyboard";
import './index.css';

// Various blob types. All are imported but only the right one is loaded dynamically.
import GraphGenericShell from 'components/graphs/generic/shell/index';
import GraphGenericPublish from 'components/graphs/generic/publish/index';
import GraphDnnShell from 'components/graphs/dnn/shell/index';
import DatasetNlpShell from 'components/dataset/nlp/shell/index';
import WorkflowGenericShell from 'components/workflow/generic/shell/index';
import SchedulerGenericShell from 'components/scheduler/generic/shell/index';

const SHELLS = {
  "graph:generic":     GraphGenericShell,
  "graph:dnn":         GraphDnnShell,
  "dataset:nlp":       DatasetNlpShell,
  "workflow:generic":  WorkflowGenericShell,
  "scheduler:generic": SchedulerGenericShell,
};

export default class Blob extends Component {
  constructor(props) {
    super(props);

    this.state = {
      notFound: false,
      org: null,
      blob: null,
      revision: null,
      forcedRevision: false,
      shortcutsShow: false,
      loadBlob: this.loadBlob,
      loadLatestRevision: this.loadLatestRevision
    };

    // Guards title writes from late-arriving promise callbacks (see componentWillUnmount).
    this.unmounted = false;
  }

  componentDidMount() {
    this.syncTitle();

    this.loadOrg();

    this.unsubscribePrefs = Preferences.subscribe(() => this.forceUpdate());
    document.addEventListener("keydown", this.handleGlobalKeyDown);
  }

  componentWillUnmount() {
    this.unmounted = true;

    if (this.unsubscribePrefs) this.unsubscribePrefs();
    document.removeEventListener("keydown", this.handleGlobalKeyDown);
  }

  // Blob-level keyboard shortcuts. Ignored on auto-repeat, while typing in a
  // field (⌘/Ctrl+E overlaps macOS "end of line" on Ctrl; "?" is a literal
  // character), and while any modal is open (so we never toggle the header or
  // open the popup behind a dialog). Tab-specific shortcuts (⌘S / ⌘R / Delete /
  // Esc) live in the workflow editor/playground handlers.
  handleGlobalKeyDown = (event) => {
    if (event.repeat) return;
    if (isTypingTarget(event.target)) return;
    if (document.body.classList.contains("modal-open")) return;

    // ⌘/Ctrl + E — toggle the blob header (expand / collapse).
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey
        && (event.key === "e" || event.key === "E")) {
      event.preventDefault();
      this.toggleHeaderCollapsed();
      return;
    }

    // ? — open the keyboard-shortcuts help popup. Match the resulting character
    // (so it works however "?" is produced, e.g. Shift+/ or AltGr) as long as
    // no Command chord is held.
    if (event.key === "?" && !event.metaKey) {
      event.preventDefault();
      this.setState({ shortcutsShow: true });
    }
  };

  toggleHeaderCollapsed() {
    if (!this.state.org || !this.state.blob) return;
    const scope = this.scope();
    const current = !!Preferences.get("app.blobHeaderCollapsed", scope);
    Preferences.set("app.blobHeaderCollapsed", !current, scope);
  }

  /**
   * Both aliases come from the route params, so this is correct from first paint and is simply
   * re-derived with better data as loadOrg and loadBlob resolve.
   */
  syncTitle() {
    // A setState callback that resolves after the user has already navigated away (e.g. via the
    // footer's <Link to="/terms">) would otherwise overwrite whatever page they landed on next.
    if (this.unmounted) return;

    const { orgId, blobId } = this.props.match.params;
    Title.set(Utils.displayName(this.state.org, orgId), Utils.displayName(this.state.blob, blobId));
  }

  loadOrg() {
    const orgId = this.props.match.params.orgId;

    this.props.app.api.orgsIdGet(orgId)
      .then(response => {
        this.setState({
          org: response.data.org
        }, () => {
          this.syncTitle();
          this.loadBlob();
        });
      }, error => {
        // Org may be inaccessible when the user has direct blob membership but not org membership.
        // Proceed to load the blob; if that also fails, notFound is set there.
        this.loadBlob();
      });
  }

  loadBlob = () => {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;

    this.props.app.api.blobsIdGet(orgId, blobId)
      .then(response => {
        const blob = response.data.blob;
        const update = { blob };
        if (!this.state.org) {
          update.org = { id: blob.org_id, alias: orgId, name: null };
        }
        this.setState(update, () => {
          this.syncTitle();
          this.finishLoadingBlob();
        });
      }, error => {
        this.setState({
          notFound: true
        });
        if (!this.unmounted) Title.setNotFound();
      });
  }

  finishLoadingBlob() {
    if ( this.props.app.api.hasBlobReachedTerminalSuccessStatus(this.state.blob) ) {
      this.loadLatestRevision();
    } else if ( this.props.app.api.isBlobInUpdatingStatus(this.state.blob) ) {
      this.loadLatestRevision();
      setTimeout(() => {
        this.loadBlob();
      }, 3000);
    } else if ( this.props.app.api.hasBlobReachedTerminalFailureStatus(this.state.blob) ) {
      // Nothing for now
    } else if ( this.props.app.api.isBlobInDeletingStatus(this.state.blob) ) {
      Navigation.push(this.props.history, Navigation.orgPath(this.state.org));
    } else {
      setTimeout(() => {
        this.loadBlob();
      }, 3000);
    }
  }

  loadLatestRevision = () => {
    if ( !this.state.blob ) {
      return;
    }

    let revisionId = this.state.blob["latest_revision_id"];

    const section = this.props.match.params.section || "default";
    const sectionId = this.props.match.params.sectionId;
    if ( "revisions" == section && sectionId ) {
      revisionId = sectionId;
      this.setState({forcedRevision: true});
    }

    if ( !revisionId ) {
      return;
    }

    this.props.app.api.revisionsIdGet(revisionId)
      .then(response => {
        this.setState({
          revision: response.data.revision
        });
      }, error => {});
  }

  /**
   * Tab orchestration
   */

  shellKey() {
    if (!this.state.blob) return null;
    return `${this.state.blob.domain}:${this.state.blob.type}`;
  }

  resolveShell() {
    return SHELLS[this.shellKey()] || null;
  }

  buildDescriptors() {
    const Shell = this.resolveShell();
    if (!Shell || !Shell.getTabs) return [];

    const ctx = {
      blob: this.state.blob,
      revision: this.state.revision,
      app: this.props.app,
      forcedRevision: this.state.forcedRevision,
    };
    const descriptors = Shell.getTabs(ctx).slice();

    descriptors.push({ key: "revisions", title: "Revisions", icon: GoHistory });

    if (Shell.settingsDisabled) {
      descriptors.push({ key: "settings", title: "Settings", icon: GoGear, disabled: true });
    } else if (this.props.app.api.canAccessBlobSettings(this.state.blob)) {
      descriptors.push({ key: "settings", title: "Settings", icon: GoGear });
    }

    // Each descriptor carries the route its tab links to; BlobTabs renders it as a real href.
    return descriptors.map(d => ({ ...d, to: this.routeForDescriptor(d) }));
  }

  // BlobTabs' tabs are links and navigate on their own. Arrow-key traversal is the one activation
  // path that goes through react-bootstrap instead of a link, so the tab it reaches comes back here,
  // where the history lives, and takes the same route the tab's own href points at.
  navigateToDescriptor = (descriptor) => {
    if (descriptor.to) Navigation.push(this.props.history, descriptor.to);
  };

  defaultDescriptorKey(descriptors) {
    const def = descriptors.find(d => d.isDefault);
    return def ? def.key : (descriptors[0] && descriptors[0].key);
  }

  resolveActiveKey(descriptors, tabSection) {
    if (tabSection === "default") {
      return this.defaultDescriptorKey(descriptors);
    }
    const found = descriptors.find(d => d.key === tabSection);
    return found ? found.key : this.defaultDescriptorKey(descriptors);
  }

  routeForDescriptor(descriptor) {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    if (descriptor.routeFor) {
      return descriptor.routeFor({
        orgId,
        blobId,
        forcedRevision: this.state.forcedRevision,
        revision: this.state.revision,
      });
    }
    // The "default" key is a sentinel for the section-less URL — used by graph and
    // dataset shells. Other shells (workflow, scheduler) keep their tab key as the
    // URL section even when the tab is marked isDefault, so clicking the tab does not
    // hit the section-less path and trigger the workflow defaultTab redirect.
    if (descriptor.key === "default") {
      return `/${orgId}/${blobId}`;
    }
    return `/${orgId}/${blobId}/${descriptor.key}`;
  }

  /**
   * Rendering helpers
   */

  scope() {
    if (!this.state.org || !this.state.blob) return {};
    return { orgId: this.state.org.id, blobId: this.state.blob.id };
  }

  collapsed() {
    if (!this.state.org || !this.state.blob) return false;
    return !!Preferences.get("app.blobHeaderCollapsed", this.scope());
  }

  matchKey(activeKey, key) {
    return activeKey === key;
  }

  paneClass(activeKey, key) {
    return this.matchKey(activeKey, key) ? "" : "hidden";
  }

  renderCollapsedExtras(descriptors, activeKey) {
    const orgId = this.props.match.params.orgId;
    const orgLabel = Utils.displayName(this.state.org, orgId);
    const blobLabel = Utils.displayName(this.state.blob);
    return (
      <>
        <Nav className="blob-header-extras-id">
          <Nav.Link as={Link} to={`/${orgId}`} className="blob-header-extras-link">
            <GoRepo className="blob-header-extras-icon"/>
            {orgLabel}
          </Nav.Link>
          <span className="blob-header-extras-sep">/</span>
          <Nav.Link
            as={Link}
            to={`/${orgId}/${this.props.match.params.blobId}`}
            className="blob-header-extras-link blob-header-extras-link-strong"
          >
            {blobLabel}
          </Nav.Link>
        </Nav>
        <BlobTabs
          descriptors={descriptors}
          activeKey={activeKey}
          onKeyboardTraverse={this.navigateToDescriptor}
          compact
        />
        <BlobHeaderToggle scope={this.scope()} compact/>
      </>
    );
  }

  render() {
    if ( this.state.notFound ) {
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="warning">Requested blob has not been found.</Alert>
          </Container>
        </>
      );
    }

    if ( null == this.state.blob ) {
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="primary">Loading blob...</Alert>
          </Container>
        </>
      );
    } else {
      if ( this.props.app.api.hasBlobReachedTerminalSuccessStatus(this.state.blob) ) {
        // Just moving on.
      } else if ( this.props.app.api.isBlobInUpdatingStatus(this.state.blob) ) {
        // Just moving on.
      } else if ( this.props.app.api.hasBlobReachedTerminalFailureStatus(this.state.blob) ) {
        return (
          <>
            <Container className="blob-header-row header-message-row">
              <Alert variant="warning">Failed to create the blob. Please try again.</Alert>
            </Container>
          </>
        );
      } else {
        return (
          <>
            <Container className="blob-header-row header-message-row">
              <Alert variant="info" className="mb-0">The blob is under construction. Please wait...</Alert>
            </Container>
          </>
        );
      }
    }

    const section = this.props.match.params.section || "default";
    const sectionId = this.props.match.params.sectionId;

    let tabSection = section;
    if ( "revisions" == section && sectionId ) {
      tabSection = "default";
    }

    // Apply default tab preference for workflow blobs
    if (tabSection === "default" && this.state.blob.domain === "workflow") {
      const scope = { orgId: this.state.org.id, blobId: this.state.blob.id };
      const preferredTab = Preferences.get("blob:workflow.defaultTab", scope);
      const validTabs = ["workflow", "debugger", "playground", "revisions", "settings"];
      if (preferredTab && preferredTab !== "workflow" && validTabs.includes(preferredTab)) {
        const orgId = this.props.match.params.orgId;
        const blobId = this.props.match.params.blobId;
        Navigation.push(this.props.history, `/${orgId}/${blobId}/${preferredTab}`);
        return null;
      }
    }

    const descriptors = this.buildDescriptors();
    const activeKey = this.resolveActiveKey(descriptors, tabSection);
    const collapsed = this.collapsed();
    const domain = this.state.blob.domain;
    const type = this.state.blob.type;

    return (
      <>
        <ShortcutsModal
          show={this.state.shortcutsShow}
          onHide={() => this.setState({ shortcutsShow: false })}
          blob={this.state.blob}
        />

        {!collapsed &&
          <BlobHeader
            app={this.props.app}
            org={this.state.org}
            blob={this.state.blob}
            revision={this.state.revision}
            match={this.props.match}
            scope={this.scope()}
          />
        }

        {!collapsed &&
          <BlobTabs
            descriptors={descriptors}
            activeKey={activeKey}
            onKeyboardTraverse={this.navigateToDescriptor}
          />
        }

        {collapsed &&
          <HeaderSlotPortal targetId="blob-extras">
            {this.renderCollapsedExtras(descriptors, activeKey)}
          </HeaderSlotPortal>
        }

        {this.state.revision && "graph" === domain && "generic" === type &&
          <>
            <div className={this.paneClass(activeKey, "default")}>
              <GraphGenericShell
                app={this.props.app}
                blob={this.state.blob}
                revision={this.state.revision}
                section={section}
                location={this.props.location}
              />
            </div>
            <div className={this.paneClass(activeKey, "publish")}>
              <GraphGenericPublish
                app={this.props.app}
                blob={this.state.blob}
                revision={this.state.revision}
                section={section}
                sectionId={sectionId}
                match={this.props.match}
              />
            </div>
          </>
        }

        {this.state.revision && "graph" === domain && "dnn" === type &&
          <div className={this.paneClass(activeKey, "default")}>
            <GraphDnnShell
              app={this.props.app}
              org={this.state.org}
              blob={this.state.blob}
              revision={this.state.revision}
              section={section}
              location={this.props.location}
            />
          </div>
        }

        {this.state.revision && "dataset" === domain && "nlp" === type &&
          <div className={this.paneClass(activeKey, "default")}>
            <DatasetNlpShell
              app={this.props.app}
              blob={this.state.blob}
              revision={this.state.revision}
              section={section}
              location={this.props.location}
            />
          </div>
        }

        {this.state.revision && "workflow" === domain && "generic" === type &&
          <WorkflowGenericShell
            app={this.props.app}
            org={this.state.org}
            blob={this.state.blob}
            revision={this.state.revision}
            match={this.props.match}
            history={this.props.history}
            location={this.props.location}
            headerCollapsed={collapsed}
          />
        }

        {this.state.revision && "scheduler" === domain && "generic" === type &&
          <SchedulerGenericShell
            app={this.props.app}
            org={this.state.org}
            blob={this.state.blob}
            revision={this.state.revision}
            match={this.props.match}
            history={this.props.history}
            location={this.props.location}
          />
        }

        {!this.state.revision && ["default", "debugger", "playground", "schedules", "workflow"].includes(activeKey) &&
          <Container className="blob-header-row content-row">
            <Alert variant="primary">Loading revision...</Alert>
          </Container>
        }

        <div className={this.paneClass(activeKey, "revisions")}>
          <BlobRevisions
            app={this.props.app}
            blob={this.state.blob}
            revision={this.state.revision}
            match={this.props.match}
            blobInterface={this.state}
          />
        </div>

        <div className={this.paneClass(activeKey, "settings")}>
          {(this.state.blob && this.props.app.api.canAccessBlobSettings(this.state.blob)) &&
            <BlobSettings
              app={this.props.app}
              org={this.state.org}
              blob={this.state.blob}
              revision={this.state.revision}
              match={this.props.match}
              history={this.props.history}
              blobInterface={this.state}
            />
          }
        </div>

        {this.state.revision &&
          <>
            <div className={["default", "workflow"].includes(activeKey) ? "" : "hidden"}>
              <BlobMarkdown
                app={this.props.app}
                blob={this.state.blob}
                revision={this.state.revision}
                match={this.props.match}
                metadata="readme"
                title="Readme"
                createPrompt="Describe this blob's contents and how to integrate with it."
              />
            </div>

            <div className={["default", "workflow"].includes(activeKey) ? "" : "hidden"}>
              <BlobMarkdown
                app={this.props.app}
                blob={this.state.blob}
                revision={this.state.revision}
                match={this.props.match}
                metadata="license"
                title="License"
                createPrompt="Tell adopters how they may use and attribute this blob."
              />
            </div>
          </>
        }
      </>
    );
  }
}
